/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray, extends as _extends, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button, MenuItem } from '@carbon/react';
import PageHeaderSkeleton from './skeleton.js';
import ActionsPanel from './children/ActionsPanel/ActionsPanel.js';
import Breadcrumbs from './children/Breadcrumbs/Breadcrumbs.js';
import SurfacedDetails from './children/SurfacedDetails/SurfacedDetails.js';

/**
 * The Page Header Component that can be used across IBM Cloud micro-services and pages built by service teams.
 * The Page Header Component can also be expanded upon to create connected components.
 */
var PageHeader = function PageHeader(_ref) {
  var breadcrumbs = _ref.breadcrumbs,
    children = _ref.children,
    className = _ref.className,
    icon = _ref.icon,
    hasIconBackground = _ref.hasIconBackground,
    isSticky = _ref.isSticky,
    _ref$linkComponent = _ref.linkComponent,
    linkComponent = _ref$linkComponent === void 0 ? 'a' : _ref$linkComponent,
    surfacedDetails = _ref.surfacedDetails,
    hasSurfacedDetailsList = _ref.hasSurfacedDetailsList,
    title = _ref.title,
    wrappedDetails = _ref.wrappedDetails,
    crn = _ref.crn,
    mock = _ref.mock,
    actionButtonDefs = _ref.actionButtons,
    actionMenuItemDefs = _ref.actionMenuItems,
    light = _ref.light;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    actionButtons = _useState2[0],
    setActionButtons = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    actionMenuItems = _useState4[0],
    setActionMenuItems = _useState4[1];
  var headerClasses = classnames("cpc--page-header", {
    'cpc--page-header--with-icon': icon,
    // when you add an icon the page header is rendered as a provisioning header
    'cpc--page-header--sticky': isSticky,
    'cpc--page-header--wrapped-details': wrappedDetails,
    'cpc--page-header--light': light
  }, className);
  useEffect(function () {
    if (actionButtonDefs && actionButtonDefs.length > 0) {
      setActionButtons(actionButtonDefs.map(function (btn) {
        return /*#__PURE__*/React.createElement(Button, _extends({
          key: btn.label,
          size: "md",
          kind: "primary"
        }, btn), btn.label);
      }));
    } else {
      setActionButtons(undefined);
    }
  }, [actionButtonDefs]);
  useEffect(function () {
    if (actionMenuItemDefs && actionMenuItemDefs.length > 0) {
      setActionMenuItems(actionMenuItemDefs.map(function (item) {
        return /*#__PURE__*/React.createElement(MenuItem, _extends({
          label: item.label,
          className: "cpc--actions-panel-menu-item",
          key: item.label
        }, item));
      }));
    } else {
      setActionMenuItems(undefined);
    }
  }, [actionMenuItemDefs]);
  var content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "cpc--page-header__main"
  }, breadcrumbs && breadcrumbs.length > 0 && /*#__PURE__*/React.createElement(Breadcrumbs, {
    breadcrumbs: breadcrumbs,
    className: className,
    linkComponent: linkComponent
  }), /*#__PURE__*/React.createElement("div", {
    className: "cpc--page-header__title-container"
  }, /*#__PURE__*/React.createElement("div", {
    title: title,
    className: "cpc--page-header__title"
  }, /*#__PURE__*/React.createElement("h1", {
    className: "cpc--page-header__title-text"
  }, title)), (surfacedDetails || crn) && /*#__PURE__*/React.createElement(SurfacedDetails, {
    mock: mock,
    hasSurfacedDetailsList: hasSurfacedDetailsList,
    surfacedDetails: surfacedDetails,
    crn: crn
  }))), (children || actionButtons || actionMenuItems) && /*#__PURE__*/React.createElement("div", {
    className: "cpc--page-header__actions"
  }, children, actionButtons, actionMenuItems && /*#__PURE__*/React.createElement(ActionsPanel, null, actionMenuItems)));
  return /*#__PURE__*/React.createElement("header", {
    className: headerClasses
  }, icon ? /*#__PURE__*/React.createElement("div", {
    className: "cpc--page-header__container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cpc--page-header__main-container"
  }, content), /*#__PURE__*/React.createElement("div", {
    className: "cpc--page-header__icon-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames('cpc--page-header__icon', {
      'cpc--page-header__icon-background': hasIconBackground
    })
  }, icon))) : content);
};
PageHeader.propTypes = {
  /**
   * A custom class name to be applied to the page header's `header` element.
   */
  className: PropTypes.string,
  /**
   * An image or SVG to use in the page header.
   */
  icon: PropTypes.element,
  /**
   * Boolean to determine whether the provisioning header icon should have a white background. Primarily used for third party images.
   */
  hasIconBackground: PropTypes.bool,
  /**
   * Whether or not the page header should stick on top of the page after scrolling.
   */
  isSticky: PropTypes.bool,
  /**
   *  A title describing the page the user is on.
   */
  title: PropTypes.string.isRequired,
  /**
   * An array of breadcrumbs to aid navigation by showing users what page they are on in relation to the site structure.
   * Each item in the array should have at minimum a value property, then either an href property or an asButton property
   * set to true and an onClick function. See the Page header with breadcrumbs code example below
   */
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    href: PropTypes.string,
    onClick: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    asButton: PropTypes.bool,
    asCustomComponent: PropTypes.bool
  })),
  /**
   * A component or element to use for the link element. If you're using something like React Router this can be a helpful backdoor to pass
   * in your own component, so the page doesn't re-render.
   */
  linkComponent: PropTypes.elementType,
  /**
   * The single or set of components to rendered on the right hand side of the header that a user may interact with.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  /**
   * Details that are surfaced to the end user. These are appended to the end of the title.
   */
  surfacedDetails: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  /**
   * Flag to indicate that the Surfaced Details have a list that should use the common design.
   */
  hasSurfacedDetailsList: PropTypes.bool,
  /**
   * Whether or not the surfaced details should be wrapped.
   */
  wrappedDetails: PropTypes.bool,
  /**
   * Provide a CRN to automatically show resource tags and allow editing them.
   */
  crn: PropTypes.string,
  /**
   * Button property objects that define the action buttons to show in the header. Each object must contain a `label` property for the button label.
   */
  actionButtons: PropTypes.arrayOf(PropTypes.shape(_objectSpread2(_objectSpread2({}, Button.propTypes), {}, {
    label: PropTypes.string.isRequired
  }))),
  /**
   * MenuItem property objects that define the menu items to show in the Actions menu.
   */
  actionMenuItems: PropTypes.arrayOf(PropTypes.shape(MenuItem.propTypes)),
  /**
   * Whether or not we should use mock data.
   */
  mock: PropTypes.bool,
  /**
   * Boolean to determine whether the header should be rendered in light mode.
   * All pages in resource or provisioning context should be rendered in light mode.
   */
  light: PropTypes.bool
};

// components should export a skeleton
PageHeader.skeleton = PageHeaderSkeleton;
PageHeader.actionsPanel = ActionsPanel;
var PageHeader$1 = PageHeader;

export { PageHeader$1 as default };
